import { useState, useEffect, useRef } from 'react';
import { useOrderStore, useResourceStore } from '@/store';
import { SubWorkOrder } from '@/types';
import { Modal } from '@/components/UI/Modal';
import { toast } from '@/components/UI/Toast';

interface SubWorkOrderFormProps {
  isOpen: boolean;
  onClose: () => void;
  mainOrderId: string;
  editingOrder?: SubWorkOrder;
}

interface FormErrors {
  processName?: string;
  processCode?: string;
  plannedQuantity?: string;
  plannedStart?: string;
  plannedEnd?: string;
}

export function SubWorkOrderForm({ isOpen, onClose, mainOrderId, editingOrder }: SubWorkOrderFormProps) {
  const addSubWorkOrder = useOrderStore((s) => s.addSubWorkOrder);
  const updateSubWorkOrder = useOrderStore((s) => s.updateSubWorkOrder);
  const updateMainWorkOrder = useOrderStore((s) => s.updateMainWorkOrder);
  const mainWorkOrders = useOrderStore((s) => s.mainWorkOrders);
  const persons = useResourceStore((s) => s.persons);
  const machines = useResourceStore((s) => s.machines);
  const materials = useResourceStore((s) => s.materials);

  const isEditMode = !!editingOrder;
  const firstInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    processName: '',
    processCode: '',
    assignedPersons: [] as string[],
    machineId: '',
    plannedQuantity: 100,
    actualQuantity: 0,
    plannedStart: '',
    plannedEnd: '',
    requiredMaterials: [] as string[],
    status: '待開啟' as SubWorkOrder['status'],
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (editingOrder) {
      setFormData({
        processName: editingOrder.processName,
        processCode: editingOrder.processCode,
        assignedPersons: editingOrder.assignedPersons,
        machineId: editingOrder.machineId || '',
        plannedQuantity: editingOrder.plannedQuantity,
        actualQuantity: editingOrder.actualQuantity,
        plannedStart: editingOrder.plannedStart.slice(0, 10),
        plannedEnd: editingOrder.plannedEnd.slice(0, 10),
        requiredMaterials: editingOrder.requiredMaterials,
        status: editingOrder.status,
      });
    } else {
      setFormData({
        processName: '',
        processCode: '',
        assignedPersons: [],
        machineId: '',
        plannedQuantity: 100,
        actualQuantity: 0,
        plannedStart: '',
        plannedEnd: '',
        requiredMaterials: [],
        status: '待開啟',
      });
      setErrors({});
      setTouched(new Set());
    }
  }, [editingOrder, isOpen]);

  useEffect(() => {
    if (isOpen) firstInputRef.current?.focus();
  }, [isOpen]);

  const validateField = (name: string, value: any): string => {
    switch (name) {
      case 'processName':
        return !value?.trim() ? '請輸入工序名稱' : '';
      case 'processCode':
        return !value?.trim() ? '請輸入工序代碼' : '';
      case 'plannedQuantity':
        return (!value || value <= 0) ? '數量必須大於 0' : '';
      case 'plannedStart':
        return !value ? '請選擇開始時間' : '';
      case 'plannedEnd':
        if (!value) return '請選擇結束時間';
        if (formData.plannedStart && new Date(value) <= new Date(formData.plannedStart))
          return '結束時間必須晚於開始時間';
        return '';
      default:
        return '';
    }
  };

  const handleChange = (name: string, value: any) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (touched.has(name)) {
      const error = validateField(name, value);
      setErrors((prev) => {
        const next = { ...prev };
        if (error) (next as any)[name] = error;
        else delete (next as any)[name];
        return next;
      });
    }
  };

  const handleBlur = (name: string) => {
    setTouched((prev) => new Set([...prev, name]));
    const error = validateField(name, (formData as any)[name]);
    setErrors((prev) => {
      const next = { ...prev };
      if (error) (next as any)[name] = error;
      else delete (next as any)[name];
      return next;
    });
  };

  const toggleArrayItem = (arr: string[], item: string) =>
    arr.includes(item) ? arr.filter((i) => i !== item) : [...arr, item];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: FormErrors = {};
    for (const key of ['processName', 'processCode', 'plannedQuantity', 'plannedStart', 'plannedEnd'] as const) {
      const error = validateField(key, (formData as any)[key]);
      if (error) (newErrors as any)[key] = error;
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched(new Set(Object.keys(newErrors)));
      return;
    }

    if (isEditMode && editingOrder) {
      updateSubWorkOrder(editingOrder.id, {
        processName: formData.processName,
        processCode: formData.processCode,
        assignedPersons: formData.assignedPersons,
        machineId: formData.machineId || null,
        plannedQuantity: formData.plannedQuantity,
        actualQuantity: formData.actualQuantity,
        plannedStart: new Date(formData.plannedStart).toISOString(),
        plannedEnd: new Date(formData.plannedEnd).toISOString(),
        requiredMaterials: formData.requiredMaterials,
        status: formData.status,
      });
      toast.success('派工單已更新', `${editingOrder.id} 更新成功`);
    } else {
      const newId = `SW-${Date.now()}`;
      const newSWO: SubWorkOrder = {
        id: newId,
        mainOrderId,
        processName: formData.processName,
        processCode: formData.processCode,
        assignedPersons: formData.assignedPersons,
        machineId: formData.machineId || null,
        plannedStart: new Date(formData.plannedStart).toISOString(),
        plannedEnd: new Date(formData.plannedEnd).toISOString(),
        plannedQuantity: formData.plannedQuantity,
        actualQuantity: 0,
        status: '待開啟',
        isOvertime: false,
        requiredMaterials: formData.requiredMaterials,
      };
      addSubWorkOrder(newSWO);
      // 同步更新父制令單的 subWorkOrders 列表
      const parentMWO = mainWorkOrders.find((m) => m.id === mainOrderId);
      if (parentMWO) {
        updateMainWorkOrder(mainOrderId, {
          subWorkOrders: [...parentMWO.subWorkOrders, newId],
        });
      }
      toast.success('派工單已建立', `${newId} 建立成功`);
    }
    onClose();
  };

  const inputClass = (field: string) => {
    const base = 'w-full px-4 py-2.5 bg-white border-2 rounded-xl text-sm font-medium transition-all focus:ring-2 focus:ring-offset-0 focus:outline-none';
    return `${base} ${(errors as any)[field] ? 'border-red-300 focus:ring-red-400' : 'border-slate-200 focus:ring-blue-400 focus:border-blue-300'}`;
  };

  const activePersons = persons.filter((p) => p.status === '啟用');
  const activeMachines = machines.filter((m) => m.status === '啟用');

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEditMode ? '編輯派工單' : '新增派工單'} size="lg">
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              工序名稱 <span className="text-red-500">*</span>
            </label>
            <input
              ref={firstInputRef}
              type="text"
              value={formData.processName}
              onChange={(e) => handleChange('processName', e.target.value)}
              onBlur={() => handleBlur('processName')}
              className={inputClass('processName')}
              placeholder="例如：砂型鑄造"
            />
            {errors.processName && touched.has('processName') && (
              <p className="mt-1 text-sm text-red-600">{errors.processName}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              工序代碼 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.processCode}
              onChange={(e) => handleChange('processCode', e.target.value)}
              onBlur={() => handleBlur('processCode')}
              className={inputClass('processCode')}
              placeholder="例如：CAST-01"
            />
            {errors.processCode && touched.has('processCode') && (
              <p className="mt-1 text-sm text-red-600">{errors.processCode}</p>
            )}
          </div>
        </div>

        {/* 負責人員（多選） */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">負責人員</label>
          <div className="flex flex-wrap gap-2 p-3 bg-white border-2 border-slate-200 rounded-xl max-h-32 overflow-y-auto">
            {activePersons.map((p) => (
              <label
                key={p.id}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all border ${
                  formData.assignedPersons.includes(p.id)
                    ? 'bg-blue-100 text-blue-700 border-blue-300'
                    : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                }`}
              >
                <input
                  type="checkbox"
                  checked={formData.assignedPersons.includes(p.id)}
                  onChange={() => handleChange('assignedPersons', toggleArrayItem(formData.assignedPersons, p.id))}
                  className="sr-only"
                />
                {p.name}
              </label>
            ))}
          </div>
        </div>

        {/* 設備 */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">使用設備</label>
          <select
            value={formData.machineId}
            onChange={(e) => handleChange('machineId', e.target.value)}
            className="w-full px-4 py-2.5 bg-white border-2 border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-400 focus:border-blue-300 transition-all focus:outline-none"
          >
            <option value="">不指定設備</option>
            {activeMachines.map((m) => (
              <option key={m.id} value={m.id}>
                {m.id} - {m.name} ({m.type})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            計畫數量 <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min="1"
            value={formData.plannedQuantity}
            onChange={(e) => handleChange('plannedQuantity', Number(e.target.value))}
            onBlur={() => handleBlur('plannedQuantity')}
            className={inputClass('plannedQuantity')}
          />
          {errors.plannedQuantity && touched.has('plannedQuantity') && (
            <p className="mt-1 text-sm text-red-600">{errors.plannedQuantity}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              開始時間 <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.plannedStart}
              onChange={(e) => handleChange('plannedStart', e.target.value)}
              onBlur={() => handleBlur('plannedStart')}
              className={inputClass('plannedStart')}
            />
            {errors.plannedStart && touched.has('plannedStart') && (
              <p className="mt-1 text-sm text-red-600">{errors.plannedStart}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              結束時間 <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.plannedEnd}
              onChange={(e) => handleChange('plannedEnd', e.target.value)}
              onBlur={() => handleBlur('plannedEnd')}
              className={inputClass('plannedEnd')}
              min={formData.plannedStart}
            />
            {errors.plannedEnd && touched.has('plannedEnd') && (
              <p className="mt-1 text-sm text-red-600">{errors.plannedEnd}</p>
            )}
          </div>
        </div>

        {/* 狀態與實際產量（僅編輯模式） */}
        {isEditMode && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">狀態</label>
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className="w-full px-4 py-2.5 bg-white border-2 border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-400 focus:border-blue-300 transition-all focus:outline-none"
              >
                <option value="待開啟">待開啟</option>
                <option value="進行中">進行中</option>
                <option value="已完成">已完成</option>
                <option value="資源不足">資源不足</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">實際產量</label>
              <input
                type="number"
                min="0"
                value={formData.actualQuantity}
                onChange={(e) => handleChange('actualQuantity', Number(e.target.value))}
                className="w-full px-4 py-2.5 bg-white border-2 border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-400 focus:border-blue-300 transition-all focus:outline-none"
              />
            </div>
          </div>
        )}

        {/* 所需物料（多選） */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">所需物料</label>
          <div className="flex flex-wrap gap-2 p-3 bg-white border-2 border-slate-200 rounded-xl max-h-32 overflow-y-auto">
            {materials.map((m) => (
              <label
                key={m.id}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-all border ${
                  formData.requiredMaterials.includes(m.id)
                    ? 'bg-amber-100 text-amber-700 border-amber-300'
                    : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                }`}
              >
                <input
                  type="checkbox"
                  checked={formData.requiredMaterials.includes(m.id)}
                  onChange={() => handleChange('requiredMaterials', toggleArrayItem(formData.requiredMaterials, m.id))}
                  className="sr-only"
                />
                {m.name}
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 bg-gradient-to-r from-slate-100 to-slate-200 text-slate-700 rounded-xl hover:from-slate-200 hover:to-slate-300 transition-all text-sm font-semibold"
          >
            取消
          </button>
          <button
            type="submit"
            className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl hover:from-blue-700 hover:to-blue-600 transition-all text-sm font-semibold shadow-lg shadow-blue-500/30 active:scale-95"
          >
            {isEditMode ? '儲存變更' : '新增派工單'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
